import type { ClipboardShare } from '../clipboard/useClipboardShare';
import { IS_MAC_VIEWER } from '../clipboard/useClipboardShare';

/** 단축키 안내 문구(뷰어 OS 에 맞춰). */
const SHORTCUT_HINT = IS_MAC_VIEWER ? 'Cmd/Ctrl + V · C' : 'Ctrl + V · C';

// 세션 도구막대의 클립보드 공유(설계서 §9.3.1). 로직은 `useClipboardShare` 에 있고, 여기는 그리기만 한다.
//
// 2026-10-12 사용자 요구로 [📋 클립보드] 단추는 막대에서 빠지고 [⋯] 메뉴 안의 한 구역이 됐다 —
//   · `ClipboardToast`      = 결과 알림. **막대에 남는다**(메뉴를 닫은 뒤에 결과가 나오므로).
//   · `ClipboardMenuSection` = 주고받기 두 항목 + 단축키 켬/끔. [⋯] 판 안에 그린다.
//
// @param refocus 누른 뒤 키보드 캡처(숨김 입력칸)로 포커스를 되돌린다 — 안 그러면 다음 키가 원격에 가지 않는다

/** 클립보드 작업 결과 알림(막대 위). 알림이 없으면 아무것도 그리지 않는다. */
export function ClipboardToast({ clip, refocus }: { clip: ClipboardShare; refocus: () => void }) {
  if (!clip.toast) {
    return null;
  }
  return (
    <span className={`clip-toast ${clip.toast.kind}`} role="status" title={clip.toast.text}>
      <span className="clip-toast-text">{clip.toast.text}</span>
      {clip.toast.action && (
        <button
          className="ghost"
          onClick={() => {
            clip.toast?.action?.run();
            refocus();
          }}
        >
          {clip.toast.action.label}
        </button>
      )}
      <button
        className="clip-toast-close"
        aria-label="닫기"
        onClick={() => {
          clip.dismissToast();
          refocus();
        }}
      >
        ×
      </button>
    </span>
  );
}

/**
 * [⋯] 메뉴 안의 클립보드 구역. 구버전 에이전트(clip 채널 없음)면 그리지 않는다.
 * @param close 메뉴를 닫는다(작업 항목만 닫는다 — 단축키 켬/끔은 바뀐 상태를 눈으로 확인하도록 열어 둔다)
 */
export function ClipboardMenuSection({ clip, close }: { clip: ClipboardShare; close: () => void }) {
  if (!clip.available) {
    return null;
  }
  /** 항목 실행: 메뉴를 닫고 작업을 시작한다(작업은 사용자 동작 안에서 시작해야 브라우저가 클립보드를 허용한다). */
  const run = (fn: () => Promise<void> | void) => {
    const p = fn();
    close();
    void p;
  };
  return (
    <div className="bar-menu-section">
      <div className="bar-menu-head">📋 클립보드{clip.busy ? ' — 전송 중…' : ''}</div>
      <button className="ghost" role="menuitem" disabled={clip.busy} onClick={() => run(clip.sendLocal)}>
        내 클립보드 → 원격
        <small>원격 클립보드에 넣습니다(붙여넣기는 원격에서)</small>
      </button>
      <button className="ghost" role="menuitem" disabled={clip.busy} onClick={() => run(clip.fetchRemote)}>
        원격 클립보드 → 내 PC
        <small>원격에서 복사해 둔 내용을 가져옵니다</small>
      </button>
      <label className="bar-menu-check">
        <input type="checkbox" checked={clip.shortcutsOn} onChange={clip.toggleShortcuts} />
        단축키로 주고받기 ({SHORTCUT_HINT})
      </label>
    </div>
  );
}
